import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, merge } from 'rxjs';
import { CommentDialogComponent } from 'src/app/dialogs';
import { GoalPeriodStatus } from 'src/app/enums/goal-period-status.enum';
import { TopicType } from 'src/app/enums/topic-type.enum';
import { GoalPermissionsHelper } from 'src/app/helpers/goal-permissions.helper';
import { ActionProgress } from 'src/app/models/action-progress.model';
import { Action, ActionModel } from 'src/app/models/action.model';
import { CommentConfig } from 'src/app/models/comment-config.model';
import { Comment } from 'src/app/models/comment.model';
import { CommentDialogData } from 'src/app/models/dialog/comment-dialog-data.model';
import { Employee } from 'src/app/models/employee.model';
import { Goal } from 'src/app/models/goal.model';
import { GoalPeriod } from 'src/app/models/goalperiod.model';
import { State } from 'src/app/models/state.model';
import { GoalService, StateService } from 'src/app/services';
import { DateHelper } from 'src/app/shared/helpers';

@UntilDestroy()
@Component({
  selector: 'app-goal-action-edit',
  templateUrl: './goal-action-edit.component.html',
  styleUrls: ['./goal-action-edit.component.scss']
})
export class GoalActionEditComponent implements OnInit {

  @Input()
  public loading = false;

  @Input()
  public action: Action;

  @Input()
  public currentPeriod: GoalPeriod;

  @Input()
  public leaders: Employee[];

  @Input()
  public goal: Goal;  

  @Output()
  public added = new EventEmitter<Action>();

  @Output()
  public updated = new EventEmitter<Action>();

  @Output()
  public deleted = new EventEmitter();

  public months: { id: number, name: string }[] = [];
  public mainForm: FormGroup;
  public progressList: ActionProgress[] = [];
  public state: State;

  private readonly fieldsToWatch: string[] = ['name', 'weight', 'deadline', 'minExpectation'];
  
  public get currentEmployeeId(): number {
    return this.stateService.state.me.value.employee.id;
  }

  public getMonths = DateHelper.getMonths;

   public get getOwnComment(): Comment {
    return this.action?.comments.find(c => c.employeeId === this.state.me.value.employee.id) ?? null;
  }



  constructor(
    private goalService: GoalService,
    private stateService: StateService,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private cd: ChangeDetectorRef,
    private translate: TranslateService
  ) {
    this.state = this.stateService.state;
  }

  ngOnInit(): void {
    this.months = DateHelper.getMonths();
    this.createForm();

    if (this.action.id === 0) {
      this.setProgressList();
    }

    this.loadData();
    this.setupFieldChangeSubscriptions();
  }

  public loadData() {
      this.mainForm.patchValue(this.action);
      this.setFormState();
      this.updateSliderCheckboxState();
  }

  private createForm(): void {
    this.mainForm = this.fb.group({
      id: 0,
      isCommon: false,
      name: ['', [Validators.required]],
      deadline: null,
      weight: [null, [ Validators.min(1), Validators.max(100), Validators.pattern(/^\d+$/) ]],
      minExpectation: [null, [ Validators.min(1), Validators.max(100), Validators.pattern(/^\d*([.,]\d+)?$/)]]
    });
  }

  // Vezetői értékelés státusz előtt csak az önértékelő felhasználó tudja szerkeszteni az értékeléseket
  public getDisplayProgressList(): ActionProgress[] {
    if (this.currentPeriod.status > GoalPeriodStatus.SelfAssessment) {
      return this.action.progress;
    }
    return this.action.progress.filter(ap => ap.employeeId === this.currentPeriod.employeeId);
  }

  public setFormState() {
    if (!this.isAllowEditBasicFields())
    {
      //disable all controls except 'achieved'
      this.mainForm.get('name').disable();
    }

    if (!this.isAllowEditSpecFields())
    {
      this.mainForm.get('minExpectation').disable();
    }

    if (!GoalPermissionsHelper.canEditWeight(this.currentPeriod, this.state)) {
      this.mainForm.get('weight').disable();
    }

    if (!GoalPermissionsHelper.canEditDeadline(this.currentPeriod, this.state)) {
      this.mainForm.get('deadline').disable();
    }

    this.cd.detectChanges();
  }

  public createCommentConfig(action: Action): CommentConfig {
    return {
      employeeId: this.state?.me.value?.employee?.id ?? 0,
      topicId: TopicType.GoalAction,
      topicRef: action.id,
      author: this.state?.me.value?.employee?.name,
    } as CommentConfig;
  }

  public allowComment(): boolean {
    return GoalPermissionsHelper.canComment(this.currentPeriod, this.state);
  }

  public allowSliderEdit(ap: ActionProgress): boolean {
    return GoalPermissionsHelper.canActionSliderChange(this.currentPeriod, this.state, ap);
  }

  public isAllowEditBasicFields(): boolean {
    return GoalPermissionsHelper.canEditBasicFields(this.currentPeriod, this.state);
  }

  public allowDelete(): boolean {
    return GoalPermissionsHelper.canDeleteAction(this.currentPeriod, this.state);
  }

  public isAllowEditSpecFields(): boolean {
    return GoalPermissionsHelper.canGoalActionSpecFieldsEdit(this.currentPeriod, this.state);
  }

  public isSliderChecked(ap: ActionProgress): boolean {
    return !!ap.checkBoxValue;
  }

  public setSlider(ap: ActionProgress) {
    ap.progress = ap.progress === null ? (this.currentPeriod.reviewPeriod.reviewPeriodGoalSettings.sliderMin ?? 0) : null;
  }

  public setProgressList() {
    const progressList = [];
    let itemForTargetEmployee = this.action.progress.find(x => x.employeeId === this.currentPeriod.employeeId);

    if (!itemForTargetEmployee) {
      itemForTargetEmployee = this.createProgressItem(this.currentPeriod.employeeId, this.currentPeriod.employeeName);
    }

    progressList.push(itemForTargetEmployee);

    if (this.leaders && this.leaders.length > 0) {
      for (const leader of this.leaders) {
        let itemForLeader = this.action.progress.find(x => x.employeeId === leader.id);
        if (!itemForLeader) {
          itemForLeader = this.createProgressItem(leader.id, leader.name);
        }
        progressList.push(itemForLeader);
      }
    }

    this.action.progress = progressList;
  }

  public updateSliderCheckboxState() {
    this.action.progress.forEach(ap => {
      ap.checkBoxValue = ap.progress === null ? false : true;
    });
  }

  public createProgressItem(employeeId: number, employeeName: string): ActionProgress {
    return {
      id: 0,
      actionId: this.action.id,
      employeeId: employeeId,
      employeeName: employeeName,
      progress: null,
      checkBoxValue: false
    };
  }

  isValidAddAction(action: Action): boolean {
    return action.name != '';
  }

  deleteAction(goal: Goal, actionId: number) {
    if (confirm(this.translate.instant("GENERAL.DELETE_CONFIRM"))) {
      this.deleted.emit();
    }
  }

  public showCommentDialog() {
    if (!this.action) return;

    let dialogData: CommentDialogData = {
      topicId: TopicType.GoalAction, 
      topicRef: this.action.id,
      noSave: true,
      comment: this.getOwnComment ? {...this.getOwnComment } : null,
    };

    const dialogRef = this.dialog.open(CommentDialogComponent, {
      minWidth: "500px",
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((dialogResult: Comment) => {
      if (dialogResult) {         
        let comment = dialogResult.message ? {... dialogResult, author: this.state?.me.value?.employee.name} : null;
        this.manageCommentDialogResult(comment, this.action);
      }
    });
  }

  public manageCommentDialogResult(comment: Comment, entity: ActionModel) {
    const index = entity.comments.findIndex(c => c.employeeId === comment.employeeId);

    if (comment?.message) {
      if (index >= 0) {
        entity.comments[index] = comment;   // update
      } else {
        entity.comments.push(comment);  // add
      }
    } else {
      if (!comment.id) {
        entity.comments = entity.comments.filter(c => c.employeeId !== this.getOwnComment.employeeId);
      } else {
        entity.comments[index] = comment;   // update, ami törlést fog okozni a backenden
      }
    } 
  }

  progressChanged(event, ap: ActionProgress, action: Action, goal: Goal) {
    ap.progress = event.value;
  }

  private onSpecFieldsChanged(): void {
    this.action.progress.forEach(ap => {
      if (ap.checkBoxValue) {
        ap.checkBoxValue = false;
        ap.progress = null;
      }
    });
  }

  // Watch the specified fields for changes, and reset progress if any change occurs
  private setupFieldChangeSubscriptions(): void {
    const changes = this.fieldsToWatch
      .map(field => this.mainForm.get(field)?.valueChanges)
      .filter(obs => obs !== undefined);

    if (changes.length > 0) {
      merge(...changes)
        .pipe(
          debounceTime(300), // Vár 300ms-t, hogy a felhasználó "végezzen" a mezővel
          untilDestroyed(this)
        )
        .subscribe(() => this.onSpecFieldsChanged());
    }
  }
}
